/**
 * Language Context for SmartCMR
 * 
 * Provides multilingual support with:
 * - Language switching (NL/EN)
 * - Translation function (t)
 * - Persistence via localStorage and user profile
 * - Browser language detection fallback
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { translateError } from '../utils/errorTranslation';

// Import translation files
import nlTranslations from '../i18n/nl.json';
import enTranslations from '../i18n/en.json';
import deTranslations from '../i18n/de.json';
import plTranslations from '../i18n/pl.json';
import roTranslations from '../i18n/ro.json';
import bgTranslations from '../i18n/bg.json';
import trTranslations from '../i18n/tr.json';
import ruTranslations from '../i18n/ru.json';
import ukTranslations from '../i18n/uk.json';

const LanguageContext = createContext(null);

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// Supported languages with flags
export const LANGUAGES = {
  'nl-NL': { code: 'nl-NL', name: 'Nederlands', flag: '🇳🇱', translations: nlTranslations },
  'en-GB': { code: 'en-GB', name: 'English', flag: '🇬🇧', translations: enTranslations },
  'de-DE': { code: 'de-DE', name: 'Deutsch', flag: '🇩🇪', translations: deTranslations },
  'pl-PL': { code: 'pl-PL', name: 'Polski', flag: '🇵🇱', translations: plTranslations },
  'ro-RO': { code: 'ro-RO', name: 'Română', flag: '🇷🇴', translations: roTranslations },
  'bg-BG': { code: 'bg-BG', name: 'Български', flag: '🇧🇬', translations: bgTranslations },
  'tr-TR': { code: 'tr-TR', name: 'Türkçe', flag: '🇹🇷', translations: trTranslations },
  'ru-RU': { code: 'ru-RU', name: 'Русский', flag: '🇷🇺', translations: ruTranslations },
  'uk-UA': { code: 'uk-UA', name: 'Українська', flag: '🇺🇦', translations: ukTranslations }
};

// Default language
const DEFAULT_LANGUAGE = 'nl-NL';

/**
 * Get nested value from object using dot notation
 * Example: getNestedValue(obj, 'cmr.status.draft') returns obj.cmr.status.draft
 */
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
};

/**
 * Detect browser language and map to supported language
 */
const detectBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage || '';
  const langCode = browserLang.toLowerCase();
  
  // Check for Dutch
  if (langCode.startsWith('nl')) return 'nl-NL';
  // Check for English
  if (langCode.startsWith('en')) return 'en-GB';
  // Check for German
  if (langCode.startsWith('de')) return 'de-DE';
  // Check for Polish
  if (langCode.startsWith('pl')) return 'pl-PL';
  // Check for Romanian
  if (langCode.startsWith('ro')) return 'ro-RO';
  // Check for Bulgarian
  if (langCode.startsWith('bg')) return 'bg-BG';
  // Check for Turkish
  if (langCode.startsWith('tr')) return 'tr-TR';
  // Check for Russian
  if (langCode.startsWith('ru')) return 'ru-RU';
  // Check for Ukrainian
  if (langCode.startsWith('uk')) return 'uk-UA';
  
  // Default to Dutch
  return DEFAULT_LANGUAGE;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    // Check localStorage first
    const stored = localStorage.getItem('smartcmr_language');
    if (stored && LANGUAGES[stored]) {
      return stored;
    }
    // Fall back to browser detection
    return detectBrowserLanguage();
  });
  
  const [translations, setTranslations] = useState(LANGUAGES[language]?.translations || nlTranslations);

  // Update translations when language changes
  useEffect(() => {
    const langData = LANGUAGES[language];
    if (langData) {
      setTranslations(langData.translations);
      // Set HTML lang attribute for accessibility
      document.documentElement.lang = language.split('-')[0];
    }
  }, [language]);

  /**
   * Set language and persist choice
   */
  const setLanguage = useCallback(async (newLang, token = null) => {
    if (!LANGUAGES[newLang]) {
      console.warn(`Language ${newLang} not supported`);
      return;
    }
    
    // Update state
    setLanguageState(newLang);
    
    // Persist to localStorage
    localStorage.setItem('smartcmr_language', newLang);
    
    // If user is logged in, save to profile
    if (token) {
      try {
        await axios.patch(`${API_URL}/auth/me/language`, 
          { language: newLang },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error('Failed to save language preference:', error);
        // Don't throw - language still changed locally
      }
    }
  }, []);

  /**
   * Load language from user profile when logged in
   */
  const loadUserLanguage = useCallback(async (token) => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const userLang = response.data?.language;
      if (userLang && LANGUAGES[userLang]) {
        setLanguageState(userLang);
        localStorage.setItem('smartcmr_language', userLang);
      }
    } catch (error) {
      // Ignore - use local language
    }
  }, []);

  /**
   * Translation function with fallback to Dutch
   * Usage: t('cmr.status.draft') or t('cmr.validation.required', { field: 'Name' })
   * 
   * IMPORTANT: Always use full key paths that resolve to strings, not objects.
   * ❌ INVALID: t('cmr.email')  // Returns object
   * ✅ VALID:   t('cmr.email.sendButton')  // Returns string
   */
  const t = useCallback((key, params = {}) => {
    let text = getNestedValue(translations, key);
    
    // Fallback to Dutch if translation not found
    if (text === undefined) {
      text = getNestedValue(nlTranslations, key);
      if (text === undefined) {
        console.warn(`Missing translation: ${key}`);
        return key;
      }
    }
    
    // DEFENSIVE: Prevent React crash if translation resolves to an object
    // This happens when someone uses t('parent.key') instead of t('parent.key.child')
    if (typeof text === 'object' && text !== null) {
      console.error(
        `[i18n Error] Translation key "${key}" resolves to an object, not a string. ` +
        `Objects are not valid as React children. ` +
        `Available keys: ${Object.keys(text).join(', ')}. ` +
        `Use a more specific key like "${key}.${Object.keys(text)[0]}".`
      );
      // Return empty string to prevent crash, or the key for debugging
      return process.env.NODE_ENV === 'development' ? `[OBJECT: ${key}]` : '';
    }
    
    // Replace parameters like {{field}} with provided values
    if (params && typeof text === 'string') {
      Object.keys(params).forEach(param => {
        // Support both {{param}} and {param} syntax
        text = text.replace(new RegExp(`\\{\\{${param}\\}\\}`, 'g'), params[param]);
        text = text.replace(new RegExp(`\\{${param}\\}`, 'g'), params[param]);
      });
    }
    
    return text;
  }, [translations]);

  /**
   * Get current language info
   */
  const currentLanguage = LANGUAGES[language] || LANGUAGES[DEFAULT_LANGUAGE];

  const value = {
    language,
    setLanguage,
    loadUserLanguage,
    t,
    tError: useCallback((errorKey) => translateError(errorKey, t), [t]),
    currentLanguage,
    languages: Object.values(LANGUAGES),
    isRTL: false // For future RTL support
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

/**
 * Custom hook to use language context
 */
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

/**
 * Export translation function for use outside React components
 * (e.g., in utility functions)
 */
export const getTranslation = (language, key, params = {}) => {
  const langData = LANGUAGES[language] || LANGUAGES[DEFAULT_LANGUAGE];
  let text = getNestedValue(langData.translations, key);
  
  if (text === undefined) {
    return key;
  }
  
  if (params && typeof text === 'string') {
    Object.keys(params).forEach(param => {
      text = text.replace(new RegExp(`{${param}}`, 'g'), params[param]);
    });
  }
  
  return text;
};

export default LanguageContext;
